import type { ConnectedSummaryLine } from '../../../../../components/ConnectedSummaryLines'
import { ConnectedSummaryLinesV2 } from '../../../../../components/ConnectedSummaryLinesV2'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../../../../utils/appBridge'
import {
  isIdealPlansFetchFailed,
  isIdealPlansLoading,
  useLumpsumPlansContext
} from '../../LumpsumPlansProvider'
import { PlansLumpsumListEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import { HeaderHeroDescription } from '../../lumpsum-ui-components/HeaderHeroDescription'
import { HeaderHeroSkeleton } from '../../lumpsum-ui-components/HeaderHeroSkeleton'
import { HeaderRiveBackdrop } from '../common/HeaderRiveBackdrop'
import {
  getPlansFlowTheme,
  PlansThemeFlow,
  resolveHeaderAppearanceWithRiskProfile,
  type LumpsumHeaderAppearance
} from '../common/riskProfileHeaderTheme'
import type { AllocationRiskProfile } from '../../../../../expert-review/types/allocation.types'
import type { LumpsumIdealPlansResponse } from '../../../api/ideal.types'
import { PlansPlanMode } from '../../../api/types'
import { isIdealPlansListFullyEmpty } from '../../../../utils/lumpsum/planListUtils'

const IDEAL_HEADER_DESCRIPTION =
  'This is what your portfolio looks like when it\'s personalised to your life stage and risk appetite. '

export function LumpsumIdealHeader() {
  const { planMode, idealData, idealPlansQuery } = useLumpsumPlansContext()
  const headerAppearance = resolveIdealHeaderAppearance(idealData?.riskProfile)
  const isHeaderActive = planMode === PlansPlanMode.IDEAL_PORTFOLIO
  const isHeaderLoading = isIdealPlansLoading(
    PlansPlanMode.IDEAL_PORTFOLIO,
    idealData != null,
    idealPlansQuery
  )
  const isPlansEmpty = !isHeaderLoading && isIdealPlansListFullyEmpty(idealData?.categories)
  const isPlansFetchFailed = isIdealPlansFetchFailed(PlansPlanMode.IDEAL_PORTFOLIO, idealPlansQuery)

  if (isPlansEmpty || isPlansFetchFailed) {
    return null
  }

  const plansListHero = getIdealPlansListHeroFields(idealData)
  const connectedSummaryLines = isHeaderLoading
    ? []
    : getIdealConnectedSummaryLines(plansListHero)

  function handleStrategyLandingCtaClick() {
    const strategyLandingCta = idealData?.strategyLandingCta
    if (strategyLandingCta == null) return
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.LumpsumIdeal,
        eventId: PlansLumpsumListEventIds.StrategyLandingCta,
        buttonLabel: 'Strategy landing'
      }
    })
    postCTA(JSON.stringify(strategyLandingCta))
  }

  return (
    <div>
      {isHeaderLoading ? (
        <HeaderHeroSkeleton />
      ) : (
        <div className="relative overflow-visible px-5 pt-6 pb-1">
          <HeaderRiveBackdrop
            isActive={isHeaderActive}
            riveSrc={headerAppearance.riveSrc}
            headerSvgSrc={headerAppearance.headerSvgSrc}
            backgroundColor={headerAppearance.backgroundColor}
            onLandingCtaClick={
              idealData?.strategyLandingCta == null ? undefined : handleStrategyLandingCtaClick
            }
          />

          <div className="relative z-[1] flex gap-3 pointer-events-none">
            <div className="min-w-0 flex-1 max-w-[calc(100%-130px)] pointer-events-auto">
              <div className="text-[22px] font-[500] leading-[130%] text-[#232323]">
                Ideal Portfolio
              </div>

              {connectedSummaryLines.length > 0 ? (
                <div className="pt-3 pl-[2px]">
                  <ConnectedSummaryLinesV2
                    lines={connectedSummaryLines}
                    connectorColor={headerAppearance.connectorColor}
                    connectorThickness={2}
                    gap={4}
                  />
                </div>
              ) : null}

              <HeaderHeroDescription
                description={IDEAL_HEADER_DESCRIPTION}
                maxLines={3}
                className="max-w-[220px]"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

type IdealPlansListHeroFields = {
  categoryCount: number
  fundCount: number
}

function getIdealPlansListHeroFields(
  idealData: LumpsumIdealPlansResponse | undefined
): IdealPlansListHeroFields {
  const categoriesWithFunds = (idealData?.categories ?? []).filter(
    (category) => (category?.idealFunds ?? []).length > 0
  )
  const fundCountFromCategories = categoriesWithFunds.reduce(
    (fundTotal, category) => fundTotal + (category?.idealFunds ?? []).length,
    0
  )
  const idealFundCount = idealData?.idealFundCount ?? 0

  return {
    categoryCount: categoriesWithFunds.length,
    fundCount: idealFundCount > 0 ? idealFundCount : fundCountFromCategories
  }
}

function getIdealConnectedSummaryLines(
  plansListHero: IdealPlansListHeroFields
): ConnectedSummaryLine[] {
  const lines: ConnectedSummaryLine[] = []

  if (plansListHero.categoryCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatCategoryCountLabel(plansListHero.categoryCount)}</span>`
    })
  }

  if (plansListHero.fundCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatFundCountLabel(plansListHero.fundCount)}</span>`
    })
  }

  return lines
}

function formatCategoryCountLabel(categoryCount: number): string {
  return `${categoryCount} categor${categoryCount === 1 ? 'y' : 'ies'}`
}

function formatFundCountLabel(fundCount: number): string {
  return `${fundCount} fund${fundCount === 1 ? '' : 's'}`
}

const IDEAL_FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.IDEAL)
const IDEAL_HEADER_STYLE = {
  backgroundColor: IDEAL_FLOW_THEME.topColor,
  chromeBackground: IDEAL_FLOW_THEME.chromeBackground,
  reviewedOnClassName: IDEAL_FLOW_THEME.periodLabelClassName,
  connectorColor: IDEAL_FLOW_THEME.connectorColor
}

export function resolveIdealHeaderAppearance(
  riskProfile?: AllocationRiskProfile | null
): LumpsumHeaderAppearance {
  return resolveHeaderAppearanceWithRiskProfile({
    riskProfile,
    strategyPalette: 'ideal',
    ...IDEAL_HEADER_STYLE
  })
}
